import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { ApplicationContext } from '../applicationContext'

const QUERY_WAIT_INTERVAL_MS = 5000

const functionIds = new WeakMap<Function, string>()
const throttledFunctions = new Map<string, Function>()

/* Gets an identifier for a Function */
function getFunctionId(func: Function): string {
  if (!functionIds.has(func)) {
    functionIds.set(func, uuidv4())
  }
  return functionIds.get(func)
}

type Func<T extends any[]> = (...args: T) => any

/* Gets a unique key for a function based on its args */
const getInvocationKey = <T extends any[]>(func: Func<T>, args: T): string => {
  const functionId = getFunctionId(func)
  // assumption: args are serializable (will throw otherwise)
  const serializedArgs = JSON.stringify(args)
  return `${functionId}::${serializedArgs}`
}

/* Throttles a function uniquely based on its args */
const throttle = <T extends any[]>(func: Func<T>, wait: number) => {
  return function (this: ApplicationContext, ...args: T): ReturnType<Func<T>> {
    const key = getInvocationKey(func, args)

    if (!throttledFunctions.has(key)) {
      const throttledFunc = _.throttle((...throttleArgs: T) => func.apply(this, throttleArgs), wait)
      throttledFunctions.set(key, throttledFunc)
    }

    const throttledFunc = throttledFunctions.get(key)
    return throttledFunc(...args)
  }
}

export function getRecord(edgeOrId: { entityId: string } | string) {
  const entityId = _.get(edgeOrId, 'entityId', edgeOrId)

  const context = this as ApplicationContext
  const store = context?.getStore()

  return store.getRecord(entityId)
}

export function deleteRecord(edgeOrId: { entityId: string } | string) {
  const entityId = _.get(edgeOrId, 'entityId', edgeOrId)

  const context = this as ApplicationContext
  const store = context?.getStore()

  const record = store.getRecord(entityId)
  return store.deleteRecord(record)
}

export function undeleteRecord(edgeOrId: { entityId: string } | string) {
  const entityId = _.get(edgeOrId, 'entityId', edgeOrId)

  const context = this as ApplicationContext
  const store = context?.getStore()

  const record = store.getRecord(entityId)
  return store.undeleteRecord(record)
}

export function getEntity(edgeOrId: { entityId: string } | string) {
  if (!edgeOrId) {
    return null
  }
  const entityId = _.get(edgeOrId, 'entityId', edgeOrId)

  const context = this as ApplicationContext
  const store = context?.getStore()

  return store.getOrFetchRecord(entityId)
}

export async function getEntities(
  edgesOrIds?: Array<{ entityId: string } | string | null | undefined>
) {
  const context = this as ApplicationContext
  const store = context?.getStore()
  if (!store || !Array.isArray(edgesOrIds) || edgesOrIds.length === 0) {
    return []
  }
  const ids = edgesOrIds.map((edgeOrId) => {
    if (!edgeOrId) {
      return null
    }
    const id = _.get(edgeOrId, 'entityId', edgeOrId)
    return typeof id === 'string' ? id : null
  })
  const idsToFetch = ids.filter((id): id is string => Boolean(id))
  const fetched = await store.getOrFetchRecords(idsToFetch)
  const fetchedById = _.zipObject(idsToFetch, fetched)
  return ids.map((id) => (id ? fetchedById[id] ?? null : null))
}

export function getUser() {
  const context = this as ApplicationContext
  const store = context?.getStore()
  return store?.getUser()
}

export function getFacilityId () {
  const context = this as ApplicationContext
  const store = context?.getStore()
  const user = store?.getUser()
  return _.get(user, 'user.facility.entityId', null)
}

/**
 * Elastic search query.
 *
 * @param query standard ES query
 * @param formatResult
 *   true format the raw query result into a list of entities
 *   false just return the raw result without any formatting
 */
async function _queryRecords(query, formatResult = true) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  // TODO(joco) - I hit this when using queryRecords before all props are available.
  //              This is the lazy way out v.s. using conditional blocks in the UI
  //              to hide the formula field until the query is well formed.
  try {
    const rawResult = await store.queryRecords(query)
    return formatResult ? _.map(rawResult?.children, 'data') : rawResult
  } catch(e) {
    console.error(`failed to query records: ${e}`)
    return []
  }
}

/**
 * A simplify version of queryRecords to do ES "query" type
 *
 * @param entityTypeId entity type id
 * @param path path to query against
 * @param value to match
 *
 * @return a list of matched entities
 */
async function _queryRecordsBasic(entityTypeId: string, path: string, value: any) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  const query = {
    filters: [
      {
        type: 'containsEdge',
        path: 'mixins.active',
        values: [
          {
            entityId: entityTypeId,
          },
        ],
      },
      {
        path,
        type: 'query',
        value,
      },
    ],
  }
  const rawResult = await store.queryRecords(query)

  return _.map(rawResult?.children, 'data')
}

/**
 * A simplify version to search across firm
 *
 * @param firmId the firm to include into this search
 * @param entityTypeId entity type to search for
 * @param path entity property to search for
 * @param value  matching value
 */
async function _queryRecordsByFirmBasic(
  firmId: string,
  entityTypeId: string,
  path: string,
  value: any
) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  const query = {
    filters: [
      {
        type: 'matchFirm',
        value: {
          uniqueId: firmId,
        },
      },
      {
        type: 'containsEdge',
        path: 'mixins.active',
        values: [
          {
            entityId: entityTypeId,
          },
        ],
      },
      {
        path,
        type: 'query',
        value,
      },
    ],
  }
  const rawResult = await store.queryRecords(query)

  return _.map(rawResult?.children, 'data')
}

interface QueryRecordsSmartParams {
  entityIdOrUri?: string
  firmId?: string
  entityIdsOrUris?: string[]
  computations?: any
  filters?: any[]
  groups?: any[]
  orders?: any[]
  queryPath?: string
  queryPaths?: string[]
  queryType?: string
  maxSizePerGroup?: number
  size?: number
  shouldIncludeLeafEntities?: boolean
  shouldStripMetadata?: boolean
  query?: any
}

async function _queryRecordsAll({
  entityIdOrUri,
  firmId,
  computations = {},
  filters = [],
  groups = [],
  orders = [],
  queryPath,
  queryPaths,
  queryType = 'query',
  maxSizePerGroup = 50,
  size = 500,
  shouldIncludeLeafEntities = true,
  shouldStripMetadata = true,
  query = {},
}: QueryRecordsSmartParams) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  const queryRequest = store.query()
    .setQuery(query)
    .setEntityType(entityIdOrUri)
    .setComputations(computations)
    .setFirm(firmId)
    .setFilters(filters)
    .setGroups(groups)
    .setOrders(orders)
    .setQueryPath(queryPath)
    .setQueryPaths(queryPaths)
    .setQueryType(queryType)
    .setMaxSizePerGroup(maxSizePerGroup)
    .setSize(size)
    .setShouldIncludeLeafEntities(shouldIncludeLeafEntities)
    .setShouldStripMetadata(shouldStripMetadata)
    .setDebug({ context: 'queryRecords--' + (entityIdOrUri ?? 'unknown') })

  const collection = queryRequest.getCollection()

  await collection.find()

  while (collection.hasNext()) {
    await collection.next()
  }

  return collection.content
}

export const queryRecords = throttle(_queryRecords, QUERY_WAIT_INTERVAL_MS)
export const queryRecordsBasic = throttle(_queryRecordsBasic, QUERY_WAIT_INTERVAL_MS)
export const queryRecordsByFirmBasic = throttle(_queryRecordsByFirmBasic, QUERY_WAIT_INTERVAL_MS)
export const queryRecordsAll = throttle(_queryRecordsAll, QUERY_WAIT_INTERVAL_MS)

export function postParamsAndJSON(...params) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  return store.getApi().postParamsAndJSON(...params)
}
